import { validateStep } from '@/lib/assessmentValidation';
import { useAssessmentState } from '@/hooks/useAssessmentState';
import { useAssessmentSubmit } from '@/hooks/useAssessmentSubmit';
import { useStepNavigation } from '@/hooks/useStepNavigation';

const STEP_LABELS = ['Profile', 'AWSAI Part 1', 'AWSAI Part 2', 'Extended', 'Workflows & Consent'];

const computeCompletedSteps = (currentStep, state) =>
  [1, 2, 3, 4, 5].filter(s => s < currentStep && validateStep(s, state) === null);

/**
 * Top-level orchestrator: composes the state, submit, and step-navigation
 * concerns into a single API used by AssessmentPage.
 */
export function useAssessmentForm() {
  const formState = useAssessmentState();
  const { loading, submit: runSubmit } = useAssessmentSubmit();

  // Snapshot of the values needed by validateStep.
  const state = {
    profile: formState.profile,
    awasi: formState.awasi,
    extended: formState.extended,
    workflows: formState.workflows,
    consent: formState.consent,
  };

  const nav = useStepNavigation(state);
  const progress = (nav.step / nav.totalSteps) * 100;
  const completedSteps = computeCompletedSteps(nav.step, state);

  // Wrap each state mutation so it clears any prior validation error.
  const wrap = (fn) => (...args) => { fn(...args); nav.clearError(); };
  const setProfile = wrap(formState.setProfile);
  const setConsent = wrap(formState.setConsent);
  const updateAwasi = wrap(formState.updateAwasi);
  const updateExtended = wrap(formState.updateExtended);
  const updateWorkflow = wrap(formState.updateWorkflow);

  const submit = () => (nav.validateOrToast() ? Promise.resolve() : runSubmit(state));

  return {
    step: nav.step,
    totalSteps: nav.totalSteps,
    progress,
    loading,
    stepLabels: STEP_LABELS,
    completedSteps,
    stepError: nav.stepError,
    ...state,
    setProfile, updateAwasi, updateExtended, updateWorkflow, setConsent,
    goNext: nav.goNext,
    goPrevious: nav.goPrevious,
    submit,
  };
}
